// client/src/pages/MyReportsPage.js
// REBUILT Jun 2026: Three tabs
//   1. AI Performance Report — scores table + Claude narrative (campaign snapshot)
//   2. AI Content Analysis   — per-URL entity/content PDF with AI recommendations
//   3. Reports               — unified history

import React, { useState, useEffect, useCallback } from 'react';
import SidebarLayout from '../components/SidebarLayout';

if (typeof document !== 'undefined') {
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
  fontLink.rel = 'stylesheet';
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    document.head.appendChild(fontLink);
  }
}

const colors = {
  primary:   '#39393a',
  secondary: '#048ba8',
  tertiary:  '#e9c46a',
  accent:    '#f4a261',
  grey:      '#e76f51',
  green:     '#01796f'
};

const font = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ── Score helpers ──────────────────────────────────────────────────────────────

function getStatus(value, type) {
  if (value === null || value === undefined) return { label: 'N/A', color: '#9ca3af' };
  if (type === 'position') {
    if (value <= 2)  return { label: 'Strong',  color: '#10b981' };
    if (value <= 5)  return { label: 'Medium',  color: '#f59e0b' };
    return             { label: 'Weak',    color: colors.grey };
  }
  if (value >= 70) return { label: 'Good',   color: '#10b981' };
  if (value >= 40) return { label: 'Medium', color: '#f59e0b' };
  return             { label: 'Poor',   color: colors.grey };
}

function fmtScore(value, type) {
  if (value === null || value === undefined) return 'N/A';
  if (type === 'position') return value.toFixed(1);
  return `${Math.round(value)}%`;
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Spinner = ({ color = 'white', size = 14 }) => (
  <span style={{
    display: 'inline-block', width: size, height: size,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: color, borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', flexShrink: 0
  }} />
);

// ── Score fetching (mirrors CampaignScorecard logic) ──────────────────────────

async function fetchAllScores(campaign, apiBaseUrl) {
  const brandDomains = [...new Set((campaign.urls || []).map(u => {
    const urlStr = typeof u === 'string' ? u : u?.url || '';
    try { return new URL(urlStr).hostname.replace(/^www\./, '').toLowerCase(); }
    catch { return null; }
  }).filter(Boolean))];

  const [llm, url, cg, kg] = await Promise.allSettled([
    // LLM audit
    fetch(`${apiBaseUrl}/audit-results?campaignId=${campaign.id}&moduleType=llm-audit&limit=1`)
      .then(r => r.ok ? r.json() : [])
      .then(results => {
        if (!results?.length) return { visibility: null, citation: null, position: null };
        const data = typeof results[0].result_data === 'string' ? JSON.parse(results[0].result_data) : results[0].result_data;
        const promptResults = Array.isArray(data.results) ? data.results : Array.isArray(data.results?.results) ? data.results.results : [];
        const brandMetrics = data.summary?.brandMetrics || data.results?.brandMetrics || [];
        const target = brandMetrics.find(b => b.isTarget);
        let totalWithCitations = 0, totalBrandCited = 0;
        promptResults.forEach(pr => {
          const responses = (pr.responses || []).filter(r => !r.error);
          const withCit = responses.filter(r => r.citations?.length > 0);
          totalWithCitations += withCit.length;
          totalBrandCited += withCit.filter(r => r.citations.some(c => {
            const cu = typeof c === 'string' ? c : (c.url || c.source || '');
            return brandDomains.some(d => cu.toLowerCase().includes(d));
          })).length;
        });
        return {
          visibility: target?.visibilityScore ?? null,
          citation: totalWithCitations > 0 ? Math.round((totalBrandCited / totalWithCitations) * 100) : 0,
          position: target?.averagePosition ?? null
        };
      }),
    // URL overview
    fetch(`${apiBaseUrl}/modules/overview/${campaign.id}`)
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        const results = data?.data?.results || [];
        if (!results.length) return { content: null, technical: null };
        const content = Math.round(results.reduce((s, r) => s + ((r.structuredContent||0)+(r.contentComprehensiveness||0)+(r.answerDirectness||0))/3, 0) / results.length);
        const technical = Math.round(results.reduce((s, r) => s + ((r.schema||0)+(r.authors||0)+(r.freshness||0)+(r.svo||0))/4, 0) / results.length);
        return { content, technical };
      }),
    // Content graph
    fetch(`${apiBaseUrl}/audit-results?campaignId=${campaign.id}&moduleType=content-graph&limit=1`)
      .then(r => r.ok ? r.json() : [])
      .then(results => {
        if (!results?.length) return { entityGap: null };
        const data = typeof results[0].result_data === 'string' ? JSON.parse(results[0].result_data) : results[0].result_data;
        const gm = data.gapMatrix?.matrix || [];
        const all = gm.filter(e => !e.is_competitor);
        if (!all.length) return { entityGap: null };
        const matched = all.filter(e => (e.urlsCovering||0) > 0);
        const missing = all.filter(e => (e.urlsCovering||0) === 0);
        const matchedPct = (matched.length / all.length) * 100;
        const missingPct = (missing.length / all.length) * 100;
        const yourAvg = matched.length ? matched.reduce((s,e) => s+(e.avgSupply||0),0)/matched.length : 0;
        const llmAvg = all.length ? all.reduce((s,e) => s+(e.promptSalience||0),0)/all.length : 0;
        const emphasis = llmAvg > 0 ? Math.min(100,(yourAvg/llmAvg)*100) : 0;
        return { entityGap: Math.round(matchedPct*0.4 + emphasis*0.3 + (100-missingPct)*0.3) };
      }),
    // Knowledge graph
    fetch(`${apiBaseUrl}/audit-results?campaignId=${campaign.id}&moduleType=knowledge-graph&limit=1`)
      .then(r => r.ok ? r.json() : [])
      .then(results => {
        if (!results?.length) return { knowledgeGraph: null };
        const data = typeof results[0].result_data === 'string' ? JSON.parse(results[0].result_data) : results[0].result_data;
        return { knowledgeGraph: data.summary?.avgKGIS ?? null };
      }),
  ]);

  return {
    ...(llm.status === 'fulfilled' ? llm.value : { visibility: null, citation: null, position: null }),
    ...(url.status === 'fulfilled' ? url.value : { content: null, technical: null }),
    ...(cg.status === 'fulfilled'  ? cg.value  : { entityGap: null }),
    ...(kg.status === 'fulfilled'  ? kg.value  : { knowledgeGraph: null }),
  };
}

// ── Scores table ──────────────────────────────────────────────────────────────

const SCORE_ROWS = [
  { key: 'visibility',    label: 'Visibility',       type: 'percent' },
  { key: 'citation',      label: 'Citation Rate',     type: 'percent' },
  { key: 'position',      label: 'Avg Position',      type: 'position' },
  { key: 'content',       label: 'Content',           type: 'percent' },
  { key: 'technical',     label: 'Technical',         type: 'percent' },
  { key: 'entityGap',     label: 'Entity Gap',        type: 'percent' },
  { key: 'knowledgeGraph',label: 'Knowledge Graph',   type: 'percent' },
];

const ScoresTable = ({ scores }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: colors.secondary, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
      At a Glance
    </div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead>
        <tr style={{ borderBottom: `2px solid #e5e7eb` }}>
          {['Metric', 'Score', 'Status'].map((h, i) => (
            <th key={h} style={{
              padding: '8px 12px', textAlign: i === 1 ? 'right' : 'left',
              fontSize: 11, fontWeight: 700, color: '#9ca3af',
              textTransform: 'uppercase', letterSpacing: '0.05em'
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {SCORE_ROWS.map(({ key, label, type }, i) => {
          const value = scores[key];
          const status = getStatus(value, type);
          return (
            <tr key={key} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
              <td style={{ padding: '10px 12px', fontWeight: 500, color: colors.primary }}>{label}</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: status.color, fontVariantNumeric: 'tabular-nums' }}>
                {fmtScore(value, type)}
              </td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 600, color: status.color
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: status.color, display: 'inline-block' }} />
                  {status.label}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ── Narrative renderer ────────────────────────────────────────────────────────

const NarrativeRenderer = ({ narrative }) => {
  if (!narrative) return null;
  return (
    <div style={{ lineHeight: 1.75, color: '#374151', fontSize: 14 }}>
      {narrative.split(/\n\n+/).filter(Boolean).map((para, i) => {
        if (para.startsWith('## ')) {
          return <h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '22px 0 8px' }}>{para.replace('## ', '')}</h3>;
        }
        const parts = para.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} style={{ margin: '0 0 14px' }}>
            {parts.map((part, j) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={j}>{part.slice(2, -2)}</strong>
                : part
            )}
          </p>
        );
      })}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const MyReportsPage = ({ campaign, campaigns, apiBaseUrl }) => {
  const [activeTab, setActiveTab] = useState('ai-performance');
  const [notification, setNotification] = useState(null);

  // Tab 1 — AI Performance Report
  const [scoresLoading, setScoresLoading]   = useState(false);
  const [scores, setScores]                 = useState(null);
  const [aiLoading, setAiLoading]           = useState(false);
  const [aiReport, setAiReport]             = useState(null);

  // Tab 2 — AI Content Analysis
  const [urlReportLoading, setUrlReportLoading] = useState(false);
  const [urlReportReady, setUrlReportReady]     = useState(null);

  // Tab 3 — Reports history
  const [reports, setReports]         = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [runningAgain, setRunningAgain]     = useState({});

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    setAiReport(null);
    setScores(null);
    setUrlReportReady(null);
  }, [campaign?.id]);

  // Pre-fetch scores when on AI Performance tab
  useEffect(() => {
    if (campaign?.id && activeTab === 'ai-performance' && !scores && !scoresLoading) {
      setScoresLoading(true);
      fetchAllScores(campaign, apiBaseUrl)
        .then(s => setScores(s))
        .catch(console.error)
        .finally(() => setScoresLoading(false));
    }
  }, [campaign?.id, activeTab]);

  const loadReports = useCallback(async () => {
    if (!campaign?.id || !apiBaseUrl) return;
    setReportsLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/campaigns/${campaign.id}/reports?include_archived=true`);
      if (!res.ok) throw new Error('Failed to load reports');
      const data = await res.json();
      const all = [
        ...(data.scheduled || []).map(r => ({ ...r, _scheduled: true })),
        ...(data.oneOff    || []),
        ...(data.archived  || []).map(r => ({ ...r, _archived: true })),
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setReports(all);
    } catch (err) { console.error(err); }
    finally { setReportsLoading(false); }
  }, [campaign?.id, apiBaseUrl]);

  useEffect(() => { if (activeTab === 'reports') loadReports(); }, [activeTab, loadReports]);

  // ── Tab 1: Generate AI Report ──────────────────────────────────────────────

  const handleGenerateAiReport = async () => {
    if (!campaign?.id) return;
    // Ensure scores are loaded
    if (!scores) {
      setScoresLoading(true);
      const s = await fetchAllScores(campaign, apiBaseUrl).catch(() => null);
      setScores(s);
      setScoresLoading(false);
    }
    setAiLoading(true);
    setAiReport(null);
    try {
      const res = await fetch(`${apiBaseUrl}/reports/ai-report/${campaign.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': campaign.user_id || '' }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate AI report');
      }
      const data = await res.json();
      setAiReport({ narrative: data.narrative, generatedAt: data.generatedAt });
      showNotification('AI Performance Report generated.');
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Tab 2: URL Report ──────────────────────────────────────────────────────

  const handleGenerateUrlReport = async () => {
    if (!campaign?.id) return;
    setUrlReportLoading(true);
    setUrlReportReady(null);
    try {
      const startRes = await fetch(`${apiBaseUrl}/reports/copy-brief-deck/${campaign.id}`, {
        method: 'POST', headers: { 'x-user-id': campaign.user_id || '' }
      });
      if (!startRes.ok) { const e = await startRes.json().catch(() => ({})); throw new Error(e.error || 'Failed to start'); }
      const { jobId } = await startRes.json();
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const poll = async () => {
          attempts++;
          const s = await fetch(`${apiBaseUrl}/reports/copy-brief-deck/job/${jobId}`).then(r => r.json());
          if (s.status === 'done') return resolve();
          if (s.status === 'error') return reject(new Error(s.error));
          if (attempts >= 120) return reject(new Error('Timed out'));
          setTimeout(poll, 10000);
        };
        setTimeout(poll, 10000);
      });
      setUrlReportReady({ jobId, filename: `${campaign.name.replace(/[^a-z0-9]/gi,'_')}_AI_Content_Analysis.pdf` });
      showNotification('AI Content Analysis ready — click Download to save.');
    } catch (err) { showNotification(err.message, 'error'); }
    finally { setUrlReportLoading(false); }
  };

  const handleDownloadUrlReport = async () => {
    if (!urlReportReady) return;
    const { jobId, filename } = urlReportReady;
    try {
      const dlRes = await fetch(`${apiBaseUrl}/reports/copy-brief-deck/job/${jobId}/download`);
      if (!dlRes.ok) throw new Error('Download failed');
      const blob = await dlRes.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      showNotification('AI Content Analysis downloaded.');
      setUrlReportReady(null);
    } catch (err) { showNotification(err.message, 'error'); }
  };

  // ── Tab 3: Run Again ───────────────────────────────────────────────────────

  const handleRunAgain = async (report) => {
    const isAi = (report.type||'').includes('ai') || (report.name||'').toLowerCase().includes('ai');
    setRunningAgain(prev => ({ ...prev, [report.id]: true }));
    try {
      if (isAi) { setActiveTab('ai-performance'); await handleGenerateAiReport(); }
      else { setActiveTab('url-analysis'); await handleGenerateUrlReport(); }
    } finally { setRunningAgain(prev => ({ ...prev, [report.id]: false })); }
  };

  // ── Print/Download AI report ───────────────────────────────────────────────

  const handlePrintReport = () => window.print();

  // ── Render ─────────────────────────────────────────────────────────────────

  const tabs = [
    { id: 'ai-performance', label: 'AI PERFORMANCE REPORT' },
    { id: 'url-analysis',   label: 'AI CONTENT ANALYSIS' },
    { id: 'reports',        label: 'REPORTS' },
  ];

  return (
    <SidebarLayout currentCampaign={campaign} campaigns={campaigns} apiBaseUrl={apiBaseUrl}>
      <div style={{ padding: '24px', fontFamily: font }} className="no-print-wrapper">

        {/* Toast */}
        {notification && (
          <div style={{
            position: 'fixed', top: '24px', right: '24px', zIndex: 9999,
            padding: '14px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', animation: 'slideIn 0.3s ease-out',
            backgroundColor: notification.type === 'error' ? '#fee2e2' : '#dcfce7',
            color: notification.type === 'error' ? '#991b1b' : '#166534',
            border: `1px solid ${notification.type === 'error' ? '#fca5a5' : '#86efac'}`
          }}>
            {notification.message}
          </div>
        )}

        {/* Tab bar */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', marginBottom: '24px' }} className="no-print">
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <nav style={{ display: 'flex', gap: '32px', padding: '0 24px' }}>
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                  padding: '16px 0', fontSize: '13px', fontWeight: '600',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                  marginBottom: '-1px', background: 'none', border: 'none', cursor: 'pointer',
                  color: activeTab === tab.id ? '#111827' : '#6b7280',
                  borderBottom: activeTab === tab.id ? `2px solid ${colors.primary}` : '2px solid transparent',
                  transition: 'all 0.2s'
                }}>{tab.label}</button>
              ))}
            </nav>
          </div>
          <div style={{ padding: '12px 24px', display: 'flex', gap: '12px', backgroundColor: `${colors.tertiary}20` }}>
            <div style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, backgroundColor: colors.tertiary, color: colors.primary }}>i</div>
            <p style={{ fontSize: 14, color: colors.primary, margin: 0, lineHeight: 1.5 }}>
              {activeTab === 'ai-performance' && <><span style={{ fontWeight: 600 }}>AI Performance Report</span> — an at-a-glance scorecard of all campaign metrics followed by a Claude-written interpretation with recommendations. Campaign snapshot view.</>}
              {activeTab === 'url-analysis'   && <><span style={{ fontWeight: 600 }}>AI Content Analysis</span> — a per-URL PDF comparing entities in LLM responses to your page content, with AI-generated recommendations at URL level. Takes 5–15 minutes.</>}
              {activeTab === 'reports'        && <><span style={{ fontWeight: 600 }}>Reports</span> — every report generated for this campaign. Run Again to produce a fresh version.</>}
            </p>
          </div>
        </div>

        {!campaign && (
          <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', padding: '16px' }}>
            <p style={{ color: '#78350f', margin: 0 }}>Select a campaign from the sidebar to use reports.</p>
          </div>
        )}

        {/* ── AI Performance Report ── */}
        {campaign && activeTab === 'ai-performance' && (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <h2 style={h2Style}>AI Performance Report</h2>
              {aiReport && (
                <button onClick={handlePrintReport} className="no-print" style={{
                  padding: '8px 16px', borderRadius: 6, border: `1px solid #e5e7eb`,
                  backgroundColor: 'white', color: colors.primary, fontSize: 13,
                  fontWeight: 600, cursor: 'pointer', fontFamily: font
                }}>
                  ↓ Save as PDF
                </button>
              )}
            </div>
            <p style={{ ...descStyle, marginBottom: 20 }}>
              Scorecard overview of all campaign metrics, followed by a plain-English interpretation with recommendations.
            </p>

            {/* Scores table */}
            {scoresLoading && (
              <div style={{ padding: '16px 0', color: '#9ca3af', fontSize: 13 }}>Loading scores…</div>
            )}
            {scores && <ScoresTable scores={scores} />}

            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 20, display: 'flex', gap: 12, alignItems: 'center' }} className="no-print">
              <button onClick={handleGenerateAiReport} disabled={aiLoading} style={{ ...btnPrimaryStyle, opacity: aiLoading ? 0.7 : 1, cursor: aiLoading ? 'not-allowed' : 'pointer' }}>
                {aiLoading ? <><Spinner /> Generating report…</> : aiReport ? 'Regenerate' : 'Generate AI Report'}
              </button>
              {aiLoading && <span style={{ fontSize: 13, color: '#6b7280' }}>Analysing your audit data — usually 30–60 seconds.</span>}
            </div>

            {aiReport && (
              <div style={{ marginTop: 28 }} id="ai-report-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e5e7eb' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: colors.secondary, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    AI Analysis — {campaign.name}
                  </span>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>{formatDate(aiReport.generatedAt)}</span>
                </div>
                <NarrativeRenderer narrative={aiReport.narrative} />
              </div>
            )}
          </div>
        )}

        {/* ── AI Content Analysis ── */}
        {campaign && activeTab === 'url-analysis' && (
          <div style={cardStyle}>
            <h2 style={h2Style}>AI Content Analysis</h2>
            <p style={descStyle}>
              A per-URL PDF that compares the entities present in LLM responses to the content on your pages.
              Uses AI to generate specific recommendations on headings, sentence structure, and content elements
              at URL level. Covers all {campaign.urls?.length || 0} URLs in this campaign.
            </p>
            {urlReportLoading && (
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 12, fontStyle: 'italic' }}>
                Generating — typically takes 5–15 minutes. You can leave this page open and come back.
              </p>
            )}
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 24, paddingTop: 20 }}>
              {urlReportReady ? (
                <button onClick={handleDownloadUrlReport} style={{ ...btnPrimaryStyle, backgroundColor: colors.green }}>
                  ↓ Download AI Content Analysis
                </button>
              ) : (
                <button onClick={handleGenerateUrlReport} disabled={urlReportLoading} style={{ ...btnPrimaryStyle, opacity: urlReportLoading ? 0.7 : 1, cursor: urlReportLoading ? 'not-allowed' : 'pointer' }}>
                  {urlReportLoading ? <><Spinner /> Generating…</> : 'Generate AI Content Analysis'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Reports history ── */}
        {campaign && activeTab === 'reports' && (
          <div style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {reportsLoading && <div style={{ padding: 48, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>Loading reports…</div>}
            {!reportsLoading && reports.length === 0 && (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: colors.primary, marginBottom: 6 }}>No reports yet</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Generate a report — it'll appear here.</div>
              </div>
            )}
            {!reportsLoading && reports.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    {['Report', 'Date', 'Type', 'Scheduled', ''].map((h, i) => (
                      <th key={i} style={{ padding: '12px 16px', textAlign: i === 4 ? 'right' : 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report, i) => {
                    const isAi = (report.type||'').includes('ai') || (report.name||'').toLowerCase().includes('ai');
                    return (
                      <tr key={report.id || i} style={{ borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={{ padding: '14px 16px', fontSize: 13, color: colors.primary, fontWeight: 500 }}>{report.name || `Report ${i + 1}`}</td>
                        <td style={{ padding: '14px 16px', fontSize: 13, color: '#6b7280' }}>{formatDate(report.created_at || report.last_run_at)}</td>
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ padding: '2px 10px', fontSize: 11, fontWeight: 600, borderRadius: 9999, backgroundColor: isAi ? `${colors.tertiary}30` : `${colors.secondary}18`, color: isAi ? '#92400e' : colors.secondary }}>
                            {isAi ? 'AI Performance' : 'AI Content'}
                          </span>
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: 13 }}>
                          {report._scheduled ? <span style={{ color: colors.green, fontWeight: 600 }}>Weekly</span> : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          <button onClick={() => handleRunAgain(report)} disabled={runningAgain[report.id]}
                            style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${colors.secondary}`, backgroundColor: 'white', color: colors.secondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: font }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = colors.secondary; e.currentTarget.style.color = 'white'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.color = colors.secondary; }}>
                            {runningAgain[report.id] ? 'Running…' : 'Run Again'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn { from { transform: translateX(80px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media print {
          .no-print, .no-print-wrapper > *:not(#print-target) { display: none !important; }
          #ai-report-content { display: block !important; }
          body { font-family: 'Work Sans', sans-serif; }
        }
      `}</style>
    </SidebarLayout>
  );
};

const cardStyle = { backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', padding: '28px 32px' };
const h2Style = { fontSize: 20, fontWeight: 700, color: '#111827', margin: '0 0 10px' };
const descStyle = { fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: 0, maxWidth: 680 };
const btnPrimaryStyle = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 24px', borderRadius: 8, border: 'none', fontFamily: font, fontWeight: 700, fontSize: 15, color: 'white', backgroundColor: colors.secondary, cursor: 'pointer' };

export default MyReportsPage;
